import { ApiError, request } from './api';

/**
 * Session handling for the API's built-in sign-in (VITE_AUTH_MODE=api).
 *
 * The session (short-lived access token + rotating refresh token) lives in
 * localStorage, like supabase-js does, so all tabs share it. Before a request
 * the access token is refreshed if it expires within a minute. Refreshes are
 * single-flight per tab; if another tab rotated the refresh token first, this
 * tab picks up the stored result instead of signing the user out.
 */
export interface StoredSession {
  access_token: string;
  refresh_token: string;
  expires_at: number; // ms
  user: { id: string; email: string; full_name: string | null };
}

interface SessionResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: StoredSession['user'];
}

export const SESSION_KEY = 'highcrm.session';

export function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

export function storeSession(res: SessionResponse | null): StoredSession | null {
  try {
    if (!res) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    const session: StoredSession = {
      access_token: res.access_token, refresh_token: res.refresh_token,
      expires_at: Date.now() + res.expires_in * 1000, user: res.user,
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}

let inFlight: Promise<StoredSession | null> | null = null;

async function refresh(current: StoredSession): Promise<StoredSession | null> {
  try {
    const res = await request<SessionResponse>('/api/auth/refresh', { method: 'POST', body: { refresh_token: current.refresh_token }, org: false, auth: false });
    return storeSession(res);
  } catch (err) {
    const latest = readSession();
    // Another tab refreshed in the meantime: use its tokens.
    if (latest && latest.refresh_token !== current.refresh_token) return latest;
    // Network trouble: keep the session and try again later.
    if (!(err instanceof ApiError) || err.status === 0 || err.status >= 500) return current;
    storeSession(null);
    return null;
  }
}

/** A valid access token, refreshing when needed; null when signed out. */
export async function getAccessToken(onSignedOut: () => void): Promise<string | null> {
  const session = readSession();
  if (!session) return null;
  if (session.expires_at - Date.now() > 60_000) return session.access_token;
  inFlight ??= refresh(session).finally(() => { inFlight = null; });
  const next = await inFlight;
  if (!next) onSignedOut();
  return next?.access_token ?? null;
}

export async function apiSignIn(email: string, password: string) {
  return storeSession(await request<SessionResponse>('/api/auth/login', { method: 'POST', body: { email, password }, org: false, auth: false }));
}

export async function apiSignUp(email: string, password: string, fullName: string, locale: string) {
  const lang = ['uz', 'ru', 'en'].includes(locale.slice(0, 2)) ? locale.slice(0, 2) : 'uz';
  return storeSession(await request<SessionResponse>('/api/auth/signup', {
    method: 'POST', body: { email, password, full_name: fullName, locale: lang }, org: false, auth: false,
  }));
}

export async function apiSignOut() {
  const session = readSession();
  storeSession(null);
  if (session) await request('/api/auth/logout', { method: 'POST', body: { refresh_token: session.refresh_token }, org: false, auth: false }).catch(() => undefined);
}
